import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import style from "./style.module.scss";
import QuestionItem from "pages/homework/component/questionItem";
import Widget from "pages/homework/component/widget";
import { getExamTypeString } from "pages/homework/utils/filter";
import { Button, Row, Col, Affix, Anchor, Icon, notification } from "antd";
import _ from "underscore";
import {
    base64toFile,
    ExamPaperInfo,
    ExamTimeType,
    faceCheck,
    getName,
    uploadToQiNiu
} from "API";
import moment from "moment";
import useRequest from "Utils/useRequest";
import { IGroupQuestion, IQuestion } from "pages/homework";
import ExamCountDown from "./components/ExamCountDown";
import FloatWebCam, { FloatWebCamInstance } from "./components/FloatWebCam";
import SwitchPageDetector from "components/SwitchPageDetector";
import { ExamContext } from "../../context";
import { isTrue } from "../../../../../Utils/filter";
import { useInterval } from "react-use";
import { StudentPhoneFileList } from "../../../../../API";

const { Link } = Anchor;

interface ExamMainPagePCProps {
    paperId?: string; //试卷id
    submitPaper: (count?: number) => void;
    saveTopic: (
        topicId: number,
        topicType: string,
        answerId: number | number[],
        value?: string,
        userAnswers?: any
    ) => void;
    onComplete: () => void;
    examPaper: ExamPaperInfo;
    second: number;
    examList: IGroupQuestion[];
    now: string;
    endDateTime: string;
    photoList: StudentPhoneFileList | null;
    meetingRoomInfo: string;
}

const ExamMainPagePC: React.FC<ExamMainPagePCProps> = ({
    paperId,
    examPaper,
    saveTopic,
    submitPaper,
    second,
    examList,
    onComplete,
    now,
    endDateTime,
    photoList,
    meetingRoomInfo
}) => {
    const { examId, examInfo, examSettings, handleDownload } =
        useContext(ExamContext);
    const ref = useRef<FloatWebCamInstance>(null);
    const [name] = useRequest(() => getName());
    const { handleSwitchScreen } = useContext(ExamContext);

    const [answeredTopics, setAnsweredTopics] = useState<number[]>([]);
    const [disabled, setDisabled] = useState(false);
    const [visible, setVisible] = useState(false);

    // 截止时间
    const examEndTime = useMemo(() => {
        const format = "YYYY-MM-DD HH:mm:ss";
        if (!examSettings || !examSettings.stuStartTime) return "";
        if (examSettings.timeType !== ExamTimeType.Free)
            return examSettings.endTime; // 非自主时间考试, 统一按截止日期算
        return moment(examSettings.stuStartTime)
            .add(examSettings.duration, "minutes")
            .format(format); // 已开始的自主考试, 截止日期为开始考试时间+考试时间
    }, [examSettings]);

    useEffect(() => {
        const list = _.reduce(
            examList,
            (memo: IQuestion[], item: IGroupQuestion) => [
                ...memo,
                ...item.list
            ],
            []
        );

        const array: number[] = [];
        list.forEach(topic => {
            if (topic.topics && topic.topics.length > 0) {
                topic.topics.forEach(topic => {
                    isAnswered(topic) && array.push(topic.topicId);
                });
            } else {
                isAnswered(topic) && array.push(topic.topicId);
            }
        });

        setAnsweredTopics(array);
    }, [examList]);

    const isEditorAnswer = (value: string | undefined) => {
        if (value === undefined) return false;
        return value !== "" && value !== "<p></p>";
    };

    const isAnswered = (item: IQuestion): boolean => {
        if (answeredTopics.includes(item.topicId)) return true;
        if (item.topicType === "4")
            return isEditorAnswer(item.answers[0].stuAnswer || "");
        if (item.topicType === "5")
            return _.some(item.answers, item =>
                isEditorAnswer(item.stuAnswer || "")
            );
        if (
            item.topics &&
            (item.topicType === "6" ||
                item.topicType === "7" ||
                item.topicType === "8")
        ) {
            return _.every(item.topics, topic => isAnswered(topic));
        }
        return _.some(item.answers, item => item.isChoose === "1");
    };

    const isTopicAnswered = (item: IQuestion) => {
        if (item.topics && item.topics.length > 0) {
            return _.every(item.topics || [], topic =>
                answeredTopics.includes(topic.topicId)
            );
        } else {
            return answeredTopics.includes(item.topicId);
        }
    };

    const handleSaveTopic = (
        topicId: number,
        topicType: string,
        answerId: number | number[],
        value?: string,
        userAnswers?: any
    ) => {
        let isAnswered = topicType === "1" || topicType === "3";

        if (typeof answerId === "object") {
            isAnswered = answerId.length !== 0;
        }

        if (topicType === "4") {
            isAnswered = isEditorAnswer(value);
        }

        if (topicType === "5") {
            isAnswered = isEditorAnswer(value);
        }

        if (!answeredTopics.includes(topicId) && isAnswered) {
            setAnsweredTopics([...answeredTopics, topicId]);
        }

        if (!isAnswered) {
            setAnsweredTopics(_.without(answeredTopics, topicId));
        }
        saveTopic(topicId, topicType, answerId, value, userAnswers);
    };

    // 提交试卷
    function handleComplete() {
        setDisabled(true);
        onComplete();
    }

    // 记录是否第一次抓拍
    const [isFirstFaceCheck, setIsFirstFaceCheck] = useState(true);

    // 每隔5分钟拍个照
    useInterval(async () => {
        if (!isTrue(examSettings?.catchPhoto)) return; // 未开启抓拍
        const snapData = ref.current?.getSnapshot() || "";
        const file = base64toFile(snapData);

        if (!examInfo || !snapData) return; // 没有考试信息或者抓拍失败
        const data = await uploadToQiNiu(file); //获取上传后七牛地址
        faceCheck(examId, data.url, paperId).catch(() => null); // 发送信息失败也不报错

        if (isFirstFaceCheck) {
            setIsFirstFaceCheck(false);
            notification.warn({
                icon: <Icon type="camera" />,
                placement: "bottomRight",
                duration: 2,
                message: "屏幕截图正在保存中..."
            });
        }
    }, 60 * 1000 * 5);

    // 考试时长
    const examDuration = useMemo(() => {
        if (!examSettings) return 120;
        if (examSettings.timeType === ExamTimeType.Free)
            return examSettings.duration;
        return moment(examSettings.endTime).diff(
            examSettings.startTime,
            "minute"
        );
    }, [examSettings]);

    if (!examSettings || !examInfo) return null;

    return (
        <div className={style.root}>
            <div className={style.exam}>
                {examPaper.type === "1" && (
                    <Affix offsetTop={30}>
                        <div className={style.floatBox}>
                            <ExamCountDown
                                now={now}
                                onComplete={handleComplete}
                                endDateTime={examEndTime}
                                disabled={disabled}
                                onSubmit={submitPaper}
                                fileSize={photoList?.size || 0}
                            />

                            {isTrue(examSettings?.realtimeVideo) && (
                                <FloatWebCam ref={ref} />
                            )}
                            <SwitchPageDetector onSwitch={handleSwitchScreen} />
                        </div>
                    </Affix>
                )}

                <div className={style.examHead}>
                    <div className={style.info}>
                        <div className={style.top}>
                            <span>姓名：</span>
                            {name && name.name}
                        </div>

                        {!!meetingRoomInfo && (
                            <div className={style.room}>{meetingRoomInfo}</div>
                        )}
                    </div>

                    <h1 className={style.title}>{examPaper.name}</h1>
                    {examInfo?.type === "1" && (
                        <h3 className={style.subtitle}>
                            {examPaper.paperName || "试卷1"}
                        </h3>
                    )}
                    <div className={style.subtitle}>
                        满分：{parseFloat(examPaper.score).toFixed(1)} 分
                    </div>

                    {examPaper.type === "1" && (
                        <div className={`${style.subtitle} ${style.red}`}>
                            考试时长：{examDuration} 分钟
                        </div>
                    )}

                    {examPaper.type === "2" && (
                        <div className={`${style.subtitle} ${style.red}`}>
                            截至日期：{examPaper.endTime}
                        </div>
                    )}
                    {examPaper.type === "1" && (
                        <div className={style.answerCard}>
                            <div
                                className={style.answerCardButton}
                                onClick={() => setVisible(!visible)}>
                                答题卡 <Icon type={visible ? "up" : "down"} />
                            </div>
                            {visible && (
                                <Anchor className={style.answerCardList}>
                                    {examList.map(exam => (
                                        <div key={exam.topicType}>
                                            <div
                                                className={
                                                    style.answerCardTitle
                                                }>
                                                {getExamTypeString(
                                                    exam.topicType
                                                )}
                                                题
                                            </div>
                                            <Row>
                                                {exam.list.map(
                                                    (item, index) => (
                                                        <Col
                                                            key={index}
                                                            span={2}
                                                            onClick={() =>
                                                                setVisible(
                                                                    !visible
                                                                )
                                                            }>
                                                            <Link
                                                                key={index}
                                                                title={
                                                                    <div
                                                                        className={`${
                                                                            style.answerCardItem
                                                                        } ${
                                                                            isTopicAnswered(
                                                                                item
                                                                            )
                                                                                ? style.active
                                                                                : ""
                                                                        }`}>
                                                                        {index +
                                                                            1}
                                                                    </div>
                                                                }
                                                                href={`#${item.topicId}`}
                                                            />
                                                        </Col>
                                                    )
                                                )}
                                            </Row>
                                        </div>
                                    ))}
                                </Anchor>
                            )}
                        </div>
                    )}
                </div>
                {examPaper.type === "1" &&
                    examList.map((exam, index) => (
                        <Widget
                            key={index}
                            title={`${getExamTypeString(exam.topicType)}题`}
                            id={exam.topicType}>
                            {exam.list.map((topic, index) => (
                                <div
                                    id={topic.topicId.toString()}
                                    key={topic.topicId}>
                                    <QuestionItem
                                        item={topic}
                                        score={topic.score}
                                        key={index}
                                        topicId={topic.topicId}
                                        topicType={topic.topicType}
                                        questionIndex={index + 1}
                                        trunk={topic.trunk}
                                        answers={topic.answers}
                                        homeworkState={1}
                                        onQuestionChange={handleSaveTopic}
                                        contentText={topic.contentText}
                                        topics={topic.topics}
                                        onFileDownload={handleDownload}
                                    />
                                </div>
                            ))}
                        </Widget>
                    ))}

                {examPaper.type === "2" &&
                    examPaper.topics.map((exam, index) => (
                        <Widget
                            key={index}
                            title={exam.trunk}
                            id={exam.topicId.toString()}>
                            <QuestionItem
                                item={exam}
                                key={index}
                                topicId={exam.topicId}
                                topicType={exam.topicType}
                                trunk={""}
                                answers={exam.answers}
                                homeworkState={1}
                                onQuestionChange={handleSaveTopic}
                                contentText={exam.contentText}
                                onFileDownload={handleDownload}
                            />
                        </Widget>
                    ))}
            </div>

            {examPaper.type === "2" && (
                <Row className={style.submitButtons}>
                    <Col offset={10} span={4}>
                        <Button
                            onClick={() => submitPaper()}
                            disabled={disabled}
                            className={style.button}
                            size="large"
                            type="primary">
                            提交
                        </Button>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default ExamMainPagePC;
